import { Transaction } from '../../types';
import { PRESET_WALLETS } from './WalletSelector';

// Per-wallet balance for a coin, derived from prior transactions.
// Excludes the transaction currently being edited so the balance reflects
// the rest of the ledger (avoids the edit accidentally counting itself).
export function walletBalanceFor(
  coinId: string,
  wallet: string,
  txs: Transaction[],
  excludeTxId?: string,
): number {
  if (!coinId || !wallet) return 0;
  let bal = 0;
  for (const tx of txs) {
    if (tx.id === excludeTxId) continue;
    if (tx.coinId !== coinId) continue;
    if (tx.type === 'BUY'  && tx.wallet === wallet) bal += tx.amount;
    if (tx.type === 'SELL' && tx.wallet === wallet) bal -= tx.amount;
    if (tx.type === 'TRANSFER') {
      if (tx.fromWallet === wallet) bal -= tx.amount;
      if (tx.toWallet   === wallet) bal += tx.amount;
    }
  }
  return Math.max(0, bal);
}

// All wallet names known to the app — preset list + any used in transactions.
export function collectWallets(txs: Transaction[]): string[] {
  const set = new Set<string>(PRESET_WALLETS);
  for (const tx of txs) {
    if (tx.wallet)     set.add(tx.wallet);
    if (tx.fromWallet) set.add(tx.fromWallet);
    if (tx.toWallet)   set.add(tx.toWallet);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// Net per-wallet balances of a coin, grouping wallet-less BUY/SELL under
// "Unspecified" (mirrors how the portfolio attributes wallet allocation).
// Returns only wallets with a positive balance, largest first.
export function coinWalletBalances(coinId: string, txs: Transaction[]): { wallet: string; balance: number }[] {
  if (!coinId) return [];
  const map = new Map<string, number>();
  const bump = (w: string, delta: number) => map.set(w, (map.get(w) ?? 0) + delta);
  for (const tx of txs) {
    if (tx.coinId !== coinId) continue;
    if (tx.type === 'TRANSFER') {
      if (tx.fromWallet) bump(tx.fromWallet, -tx.amount);
      if (tx.toWallet)   bump(tx.toWallet,    tx.amount);
      continue;
    }
    const w = tx.wallet?.trim() || 'Unspecified';
    bump(w, tx.type === 'BUY' ? tx.amount : -tx.amount);
  }
  return Array.from(map.entries())
    .filter(([, bal]) => bal > 1e-9)
    .map(([wallet, balance]) => ({ wallet, balance }))
    .sort((a, b) => b.balance - a.balance);
}

// Concise coin amount formatter for dropdown labels.
export function fmtCoinShort(n: number): string {
  if (!isFinite(n) || n === 0) return '0';
  if (n >= 1)        return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  if (n >= 0.0001)   return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
  return n.toExponential(2);
}
